<template>
  <el-card :title="title">
    <div style="display: flex; justify-content: space-between">
      <template slot:extra>
        {{ extraContent }}
      </template>
      <el-button v-if="!readonly" type="primary" @click="selectAllPermissions"
        >全选</el-button
      >
    </div>
    <el-table
      :data="tableData"
      style="width: 100%"
      :span-method="objectSpanMethod"
    >
      <!-- 功能模块 Column -->
      <el-table-column label="功能模块" width="120">
        <template slot-scope="scope">
          <el-checkbox
            :key="scope.row.module.key"
            v-model="scope.row.module.checked"
            :disabled="readonly || scope.row.module.disabled"
            :indeterminate="scope.row.module.indeterminate"
            @change="handleModuleCheckboxChange(scope.row, ...arguments)"
          >
            {{ scope.row.module.title }}
          </el-checkbox>
        </template>
      </el-table-column>

      <!-- 一级分类 Column -->
      <el-table-column label="一级分类" width="180">
        <template slot-scope="scope">
          <el-checkbox
            :key="scope.row.firstCategory.key"
            v-model="scope.row.firstCategory.checked"
            :disabled="readonly || scope.row.firstCategory.disabled"
            :indeterminate="scope.row.firstCategory.indeterminate"
            @change="handleFirstCategoryCheckboxChange(scope.row, ...arguments)"
          >
            {{ scope.row.firstCategory.title }}
          </el-checkbox>
        </template>
      </el-table-column>

      <!-- 二级分类 Column -->
      <el-table-column label="二级分类">
        <template slot-scope="scope">
          <div class="checkbox-container">
            <div
              v-for="item in sortedSecondCategory(scope.row.secondCategory)"
              :key="item.key"
            >
              <el-tree
                v-if="item.children && item.children.length > 0"
                :data="item.treeData"
                :ref="`tree-${item.key}`"
                node-key="key"
                show-checkbox
                class="my-tree"
                :default-checked-keys="item.checkedKeys"
                :disabled="readonly || item.disabled"
                @check="
                  handleSecondaryCategoryTreeNodeCheck(item.key, ...arguments)
                "
                :render-content="renderTreeNode"
              >
              </el-tree>
              <el-checkbox
                v-else
                :key="item.key"
                v-model="item.checked"
                :disabled="readonly || item.disabled"
                class="my-checkbox"
                node-key="key"
                @change="
                  handleSecondaryCategoryCheckboxChange(
                    scope.row,
                    item.key,
                    ...arguments
                  )
                "
              >
                {{ item.title }}
              </el-checkbox>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <div v-if="!readonly" class="footer">
      <el-button @click="handleCancel">取消</el-button>
      <el-button @click="handleReset">重置</el-button>
      <el-button type="primary" @click="handleSubmit">确认保存</el-button>
    </div>
  </el-card>
</template>

<script>
import {
  flattenPermissions,
  getAllCheckedKeys,
  updateModule,
} from "./selectorUtils.js";
export default {
  name: "PermissionSelector",
  props: {
    title: String,
    data: Array, // Assuming 'data' is the prop used to pass permissions or table data
    readonly: Boolean,
    checkedKeys: Array,
  },
  data() {
    return {
      tableData: this.data, // Initialize table data from props
      extraContent: "", // Placeholder for any extra content
    };
  },
  created() {
    this.tableData = flattenPermissions(this.data, this.checkedKeys);
  },
  watch: {
    data: {
      handler(newVal) {
        this.tableData = flattenPermissions(newVal, this.checkedKeys);
      },
      deep: true,
    },
    checkedKeys: {
      handler(newVal) {
        this.tableData = flattenPermissions(this.data, newVal);
      },
      deep: true,
    },
  },
  methods: {
    renderTreeNode(h, { node, data }) {
      return h(
        "span",
        {
          style: node.checked ? { color: "#409EFF", fontWeight: 500 } : {},
        },
        data.label
      );
    },
    objectSpanMethod({ row, column, rowIndex, columnIndex }) {
      if (columnIndex === 0) {
        if (row.module.rowSpan > 0) {
          return {
            rowspan: row.module.rowSpan,
            colspan: 1,
          };
        } else {
          return {
            rowspan: 0,
            colspan: 0,
          };
        }
      }
    },
    sortedSecondCategory(children) {
      const sortedItems = [...children].sort((a) =>
        a.children && a.children.length > 0 ? 1 : -1
      );
      return sortedItems;
    },
    selectAllPermissions() {
      const updatedTableData = this.tableData.map((row) => {
        const updatedSecondCategory = row.secondCategory.map((item) => {
          let checkedKeys = item.children.map((c) => c.group_id);
          if (item.treeData) {
            checkedKeys.push(item.key);
            // If the second category has a treeData property, checkedKeys should contain its own key
            return {
              ...item,
              checked: true,
              checkedKeys,
            };
          } else {
            return {
              ...item,
              checked: true,
              checkedKeys,
            };
          }
        });

        return {
          ...row,
          module: {
            ...row.module,
            checked: true,
            indeterminate: false,
          },
          firstCategory: {
            ...row.firstCategory,
            checked: true,
            indeterminate: false,
          },
          secondCategory: updatedSecondCategory,
        };
      });

      this.tableData = updatedTableData;
    },
    handleCancel() {
      // Cancel action logic
      this.$emit("cancel");
    },
    handleReset() {
      // Reset selection logic
      this.$emit("reset");
    },
    handleSubmit() {
      // Submit selected permissions logic
      const keys = getAllCheckedKeys(this.tableData);
      this.$emit("submit", keys);
    },
    handleModuleCheckboxChange(record, checked) {
      const updatedTableData = this.tableData.map((row) => {
        if (row.module.key === record.module.key) {
          const updatedSecondCategory = row.secondCategory.map((item) => {
            const checkedKeys = checked
              ? item.children.map((c) => c.group_id)
              : [];

            if (item.treeData) {
              if (checked) {
                this.$refs[`tree-${item.key}`][0].setCheckedKeys(checkedKeys);
              } else {
                this.$refs[`tree-${item.key}`][0].setCheckedKeys([]);
              }
            }

            return {
              ...item,
              checked: checked,
              checkedKeys,
            };
          });

          return {
            ...row,
            module: {
              ...row.module,
              checked: checked,
              indeterminate: checked ? false : row.module.indeterminate,
            },
            firstCategory: {
              ...row.firstCategory,
              checked: checked,
              indeterminate: false,
            },
            secondCategory: updatedSecondCategory,
          };
        }
        return row;
      });

      this.tableData = updatedTableData;
    },
    // 一级分类勾选后，更新二级分类的 checkbox 和 tree 勾选状态
    updateSecondCategory(row, checked) {
      // console.log("🚀 : updateSecondCategory : row:", row);
      // console.log(
      //   "🚀 : updateSecondCategory : this.$refs['tree7']:",
      //   this.$refs["tree7"]
      // );
      row.secondCategory.forEach((item) => {
        if (item.treeData) {
          let checkedKeys = item.children.map((c) => c.group_id);
          if (checked) {
            this.$refs[`tree-${item.key}`][0].setCheckedKeys(checkedKeys);
          } else {
            this.$refs[`tree-${item.key}`][0].setCheckedKeys([]);
          }
        }
      });
      const updatedSecondCategory = row.secondCategory.map((item) => {
        return {
          ...item,
          checked,
          checkedKeys: checked ? item.children.map((c) => c.group_id) : [],
        };
      });
      return updatedSecondCategory;
    },
    // 一级分类勾选状态变更，需要更新二级分类的勾选状态和功能模块的勾选状态
    handleFirstCategoryCheckboxChange(record, checked) {
      const updatedTableData = this.tableData.map((row) => {
        if (row.firstCategory.key === record.firstCategory.key) {
          const updatedSecondCategory = this.updateSecondCategory(row, checked);
          const updatedFirstCategory = {
            ...row.firstCategory,
            checked: checked,
            indeterminate: checked ? false : row.firstCategory.indeterminate,
          };

          return {
            ...row,
            firstCategory: updatedFirstCategory,
            secondCategory: updatedSecondCategory,
          };
        }
        return row;
      });

      this.tableData = updateModule(updatedTableData);
    },
    // 勾选二级分类的节点后，更新一级分类的勾选状态
    updateFirstCategory(firstCategory, secondCategory) {
      const allChecked =
        secondCategory.length > 0 &&
        secondCategory.every((item) => {
          if (item.treeData) {
            return item.children.every((child) =>
              item.checkedKeys.includes(child.group_id)
            );
          } else {
            return item.checked;
          }
        });
      const someChecked = secondCategory.some((item) => {
        if (item.treeData) {
          return item.children.some((child) => {
            return item.checkedKeys.includes(child.group_id);
          });
        }
        return item.checked;
      });

      const updatedFirstCategory = {
        ...firstCategory,
        checked: secondCategory.length > 0 ? allChecked : firstCategory.checked, // 没有二级分类时，一级分类的勾选状态不变
        // : this.checkedKeys.includes(firstCategory.key),
        indeterminate: someChecked && !allChecked,
      };

      return updatedFirstCategory;
    },
    // 二级分类树组件勾选
    handleSecondaryCategoryTreeNodeCheck(pid, node, state) {
      const checkedKeys = state.checkedKeys;
      const updatedTableData = this.tableData.map((moduleItem) => {
        const secondCategories = moduleItem.secondCategory.map(
          (secondCategoryItem) => {
            if (
              secondCategoryItem.treeData &&
              (secondCategoryItem.key === node.key ||
                pid === secondCategoryItem.key)
            ) {
              return {
                ...secondCategoryItem,
                checked: checkedKeys.includes(secondCategoryItem.key),
                checkedKeys,
              };
            } else {
              return secondCategoryItem;
            }
          }
        );
        const updatedFirstCategory = this.updateFirstCategory(
          moduleItem.firstCategory,
          secondCategories
        );
        return {
          ...moduleItem,
          firstCategory: updatedFirstCategory,
          secondCategory: secondCategories,
        };
      });
      this.tableData = updateModule(updatedTableData);
    },
    handleSecondaryCategoryCheckboxChange(record, secondCategorykey, checked) {
      const updatedTableData = this.tableData.map((row) => {
        if (row.module.key === record.module.key) {
          const updatedSecondCategory = row.secondCategory.map((item) => {
            if (item.key === secondCategorykey) {
              return {
                ...item,
                checked: checked,
              };
            }
            return item;
          });
          const updatedFirstCategory = this.updateFirstCategory(
            row.firstCategory,
            updatedSecondCategory
          );
          return {
            ...row,
            firstCategory: updatedFirstCategory,
            secondCategory: updatedSecondCategory,
          };
        }
        return row;
      });
      this.tableData = updateModule(updatedTableData);
    },
  },
  mounted() {
    // Perform any initialization or fetch data if needed
  },
};
</script>

<style scoped>
.checkbox-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.my-checkbox {
  margin-right: 16px;
  align-items: flex-end;
  display: flex;
}

.my-tree {
  margin-right: 16px;
}

.footer {
  margin-top: 32px;
}
</style>



// WEBPACK FOOTER //
// src/pages/positionPermissions/PermissionSelector.vue